import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

interface PrivacyPolicyDialogProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
}

export default function PrivacyPolicyDialog({ open, onClose, onAccept }: PrivacyPolicyDialogProps) {
  const [accepted, setAccepted] = useState(false);

  const handleAccept = () => {
    if (accepted) {
      localStorage.setItem('privacy-policy-accepted', 'true');
      onAccept();
      setAccepted(false);
    }
  };

  const handleClose = () => {
    setAccepted(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="span" fontWeight="bold">
          Privacy Policy
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="text.secondary" paragraph>
          Before continuing, please review and accept our Privacy Policy.
        </Typography>
        <Box
          sx={{
            bgcolor: 'rgba(59, 130, 246, 0.1)',
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            By using CleoLead, you agree to our data practices including:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            <li>
              <Typography variant="body2" color="text.secondary">
                Your data is encrypted and stored securely
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                Only you can access your data
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                We do not sell your personal information
              </Typography>
            </li>
          </Box>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              I have read and agree to the{' '}
              <Link
                component={RouterLink}
                to="/privacy"
                target="_blank"
                sx={{ color: 'primary.main' }}
              >
                Privacy Policy
              </Link>
            </Typography>
          }
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          disabled={!accepted}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
